/**
 * A template's name and the key made from it (Feature 73 D8, D13). The name
 * keeps to letters, digits, spaces, `_`, `-`, and `.`, with a letter or digit
 * among them, as the agent checks it, so a key can always be made from a
 * name. The key is the name lowercased, each run of other characters one `_`,
 * which is what client code reads under `linked`.
 */

const NAME = /^(?=.*[A-Za-z0-9])[A-Za-z0-9 _.-]{1,128}$/;
const KEY_MAX = 64;
// A key starts with a letter; a name that starts with a digit gets this in front.
const DIGIT_PREFIX = 'linkage_';

/** What the name may hold, said when it breaks the rule; null when it keeps it. */
export function nameProblem(name: string): string | null {
  if (!name.trim()) return 'A template needs a name.';
  if (!NAME.test(name)) {
    return 'Letters a-z and A-Z, digits, spaces, _, -, and . only, with a letter or digit.';
  }
  return null;
}

/** Cut to `max` characters, with no `_` left at the end. */
function cut(key: string, max: number): string {
  return key.slice(0, max).replace(/_+$/, '');
}

/** The key a name makes, the next free one when another template holds it. */
export function keyFromName(name: string, taken: ReadonlySet<string> = new Set()): string {
  let key = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!key) return '';
  if (/^\d/.test(key)) key = `${DIGIT_PREFIX}${key}`;
  key = cut(key, KEY_MAX);
  if (!taken.has(key)) return key;
  for (let n = 2; ; n += 1) {
    const suffix = `_${n}`;
    const next = `${cut(key, KEY_MAX - suffix.length)}${suffix}`;
    if (!taken.has(next)) return next;
  }
}
